import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { adminApi } from '../lib/api';
import { PageHeader, Loading, ErrorBox } from '../components/ui';

const SECTIONS = [
  { key: 'pricing', label: 'Pricing' },
  { key: 'cart_review', label: 'Cart review' },
  { key: 'pharmacy', label: 'Pharmacy' },
  { key: 'dispatch', label: 'Dispatch' },
  { key: 'store_rules', label: 'Store rules' },
  { key: 'team', label: 'Team' },
  { key: 'integrations', label: 'Integrations' },
];

export function SettingsPage() {
  const { section } = useParams({ strict: false }) as { section?: string };
  const qc = useQueryClient();
  const active = section ?? 'pricing';
  const settings = useQuery({ queryKey: ['admin-settings', active], queryFn: () => adminApi.settings(active) });
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data) setDraft(JSON.stringify(settings.data, null, 2));
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () => {
      const parsed = JSON.parse(draft);
      const version = (settings.data as any)?.version ?? '';
      return adminApi.settingsUpdate(active, parsed, version);
    },
    onSuccess: () => { setError(null); qc.invalidateQueries({ queryKey: ['admin-settings', active] }); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Failed to save'),
  });

  return (
    <div>
      <PageHeader title="Settings" subtitle="Pricing, review, pharmacy, dispatch, store rules, team and integrations" />
      <div className="grid grid-cols-[180px_1fr] gap-4">
        <div className="card p-2">
          {SECTIONS.map((s) => (
            <Link key={s.key} to="/settings/$section" params={{ section: s.key }} className="block px-3 py-2 rounded-lg text-sm" style={active === s.key ? { background: 'var(--warn-bg-soft)', color: 'var(--brand-dark)', fontWeight: 600 } : {}}>
              {s.label}
            </Link>
          ))}
        </div>
        <div className="card p-4">
          {settings.isLoading ? (
            <Loading />
          ) : settings.error ? (
            <ErrorBox error={settings.error} />
          ) : (
            <>
              <p className="text-xs mb-2" style={{ color: 'var(--text-faint)' }}>
                Raw settings editor (JSON) — If-Match concurrency enforced server-side.
              </p>
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={20} className="w-full font-mono text-xs" />
              {error && <p className="text-xs mt-2" style={{ color: 'var(--danger)' }}>{error}</p>}
              <button className="btn-accent mt-3" onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
